import { formatHoursHm } from './durationHours';
import { computeBookingTaxQuote } from './gst/gstRules';

/** Client booking advance = 50% of grand total (base + 18% GST). */
export const BOOKING_ADVANCE_PERCENT = 0.5;

export const MIN_WALLET_RECHARGE_INR = 1;

/** NEFT/IMPS manual bank transfer only — UPI/Card/Razorpay QR allow Rs.1 for testing. */
export const MIN_BANK_TRANSFER_INR = 5000;

export const QUICK_WALLET_RECHARGE_AMOUNTS = [1, 100, 500, 1000, 5000, 10000, 25000, 50000];

/** Sanity max for attachment hours on hourly additive path (not tied to bucket-only). */
export const ATTACHMENT_HOURS_SANITY_MAX = 24;

/** Fixed hours-per-unit when rate_per_hour is missing or zero. */
export const FALLBACK_HOURS_PER_UNIT = {
  hourly: 1,
  daily: 8,
  weekly: 56,
  monthly: 176,
};

function unitRateFromMachine(machine, bookingType) {
  switch (bookingType) {
    case 'hourly':
      return Number(machine?.rates?.perHour || 0);
    case 'daily':
      return Number(machine?.rates?.perDay || 0);
    case 'weekly':
      return Number(machine?.rates?.perWeek || 0);
    case 'monthly':
      return Number(machine?.rates?.perMonth || 0);
    default:
      return 0;
  }
}

/**
 * Hours equivalent of one booking unit for attachment (₹/hr) scaling.
 * Prefer owner unit rate / rate_per_hour; else fixed fallback.
 */
export function hoursEquivPerUnit(machine, bookingType) {
  const ratePerHour = Number(machine?.rates?.perHour || 0);
  const unitRate = unitRateFromMachine(machine, bookingType);
  if (ratePerHour > 0 && unitRate > 0) {
    return unitRate / ratePerHour;
  }
  return FALLBACK_HOURS_PER_UNIT[bookingType] || 1;
}

/** Positive min hours for selected attachment, or 0 when unset (no gate). */
export function readAttachmentMinHours(machine, selectedAttachment) {
  if (!selectedAttachment) return 0;
  const map = machine?.attachmentMinHours;
  if (!map || typeof map !== 'object' || Array.isArray(map)) return 0;
  const n = Number(map[selectedAttachment]);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function attachmentMinHoursBlockMessage(minHours) {
  return `Owner requires a minimum of ${formatHoursHm(minHours)} for this attachment — please increase duration or remove the attachment.`;
}

/** Job hours-equivalent for the current quantity/unit (spot). */
export function bookedJobHoursEquiv(machine, bookingType, quantity) {
  const rawQty = Number(quantity);
  const units = bookingType === 'hourly'
    ? (Number.isFinite(rawQty) && rawQty > 0 ? rawQty : 0)
    : Math.max(1, rawQty || 1);
  return units * hoursEquivPerUnit(machine, bookingType);
}

/**
 * Feasibility-first: attachment min must fit within job duration.
 * Used for daily/weekly/monthly spot only — hourly additive path does not use this.
 * @returns {string|null}
 */
export function attachmentDurationFeasibilityError({
  machine,
  bookingType,
  quantity,
  selectedAttachment,
}) {
  if (!selectedAttachment) return null;
  if (bookingType === 'hourly') return null;
  const minH = readAttachmentMinHours(machine, selectedAttachment);
  if (!(minH > 0)) return null;
  const totalHours = bookedJobHoursEquiv(machine, bookingType, quantity);
  if (!(Number.isFinite(totalHours)) || totalHours < minH) {
    return `This attachment requires at least ${formatHoursHm(minH)} — increase job Duration first`;
  }
  return null;
}

/**
 * Hourly spot: machine.minimum_hours floors total T (bucket-only + attachment).
 * @returns {string|null}
 */
export function spotHourlyTotalFloorError({ machine, totalHours }) {
  const minH = Number(machine?.minimumHours ?? machine?.minimum_hours ?? 0);
  if (!(minH > 0)) return null;
  const t = Number(totalHours);
  if (!Number.isFinite(t) || t < minH) {
    return `Minimum booking is ${formatHoursHm(minH)}. Increase bucket-only or attachment hours so the total meets the minimum.`;
  }
  return null;
}

/**
 * Eligibility gate only — billing math unchanged.
 * @returns {string|null} block message, or null when allowed
 */
export function attachmentMinHoursGateError({ machine, selectedAttachment, bookedHours }) {
  const minH = readAttachmentMinHours(machine, selectedAttachment);
  if (!(minH > 0)) return null;
  const booked = Number(bookedHours);
  if (!Number.isFinite(booked) || booked < minH) {
    return attachmentMinHoursBlockMessage(minH);
  }
  return null;
}

/**
 * Proactive UX notice when a selected attachment has a positive min-hours gate.
 * Spot compares against attachment usage hours.
 * Does not change validation — display only.
 * @param {'spot'|'contract'} mode
 * @returns {null|{ minHours: number, bookedHours: number, satisfied: boolean, message: string }}
 */
export function attachmentMinHoursNotice({ machine, selectedAttachment, bookedHours, mode }) {
  const minHours = readAttachmentMinHours(machine, selectedAttachment);
  if (!(minHours > 0)) return null;
  const booked = Number(bookedHours);
  const bookedSafe = Number.isFinite(booked) ? booked : 0;
  const satisfied = Number.isFinite(booked) && booked >= minHours;
  const durationLabel = mode === 'contract'
    ? 'guaranteed hours per cycle'
    : 'attachment usage';
  const currentLabel = mode === 'contract' ? 'GMH' : 'usage';
  const message = satisfied
    ? `This attachment requires a minimum of ${formatHoursHm(minHours)} ${durationLabel}.`
    : `This attachment requires a minimum of ${formatHoursHm(minHours)} ${durationLabel} — your current ${currentLabel} is ${formatHoursHm(bookedSafe)}, please increase it.`;
  return {
    minHours,
    bookedHours: bookedSafe,
    satisfied,
    message,
  };
}

/**
 * Attachment surcharge for spot hire (₹).
 * att₹/hr × attachmentUsageHours — not full booking duration.
 */
export function calcAttachmentSurcharge(machine, selectedAttachment, attachmentUsageHours) {
  if (!machine || !selectedAttachment) return 0;
  const rates = machine.attachmentRates;
  if (!rates || typeof rates !== 'object') return 0;
  const attHourly = Number(rates[selectedAttachment] || 0);
  if (!(attHourly > 0)) return 0;
  const usage = Number(attachmentUsageHours);
  if (!Number.isFinite(usage) || !(usage > 0)) return 0;
  return attHourly * usage;
}

export function calcBookingUnitBase(machine, bookingType, quantity) {
  if (!machine) return 0;
  const raw = Number(quantity);
  // Hourly may be fractional (HH:MM → decimal hours). Other units stay whole counts ≥ 1.
  const qty = bookingType === 'hourly'
    ? (Number.isFinite(raw) && raw > 0 ? raw : 0)
    : Math.max(1, raw || 1);
  switch (bookingType) {
    case 'hourly':
      return machine.rates.perHour * qty;
    case 'daily':
      return machine.rates.perDay * qty;
    case 'weekly':
      return machine.rates.perWeek * qty;
    case 'monthly':
      return machine.rates.perMonth * qty;
    default:
      return 0;
  }
}

export function calcBookingBaseAmount(
  machine,
  bookingType,
  quantity,
  selectedAttachment = null,
  attachmentUsageHours = null,
) {
  const usageNumForBucket = Number(attachmentUsageHours);
  const bucketOnlyHours = (bookingType === 'hourly'
    && selectedAttachment
    && Number.isFinite(usageNumForBucket)
    && usageNumForBucket > 0)
    ? Number(quantity) - usageNumForBucket
    : quantity;
  const unitBase = calcBookingUnitBase(machine, bookingType, bucketOnlyHours);
  if (!(unitBase > 0)) return 0;
  return unitBase + calcAttachmentSurcharge(
    machine,
    selectedAttachment,
    attachmentUsageHours,
  );
}

/**
 * Spot attachment usage hours validation (client).
 * Hourly + attachment: additive path — required / >0 / ≥ owner min / sanity max 24
 *   (no duration ceiling, no feasibility-vs-duration).
 * Daily/weekly/monthly + attachment: keep feasibility + usage ≤ job hours-equiv.
 * @returns {string|null} error message
 */
export function attachmentUsageHoursGateError({
  machine,
  bookingType,
  quantity,
  selectedAttachment,
  attachmentUsageHours,
}) {
  if (!selectedAttachment) return null;
  if (attachmentUsageHours === undefined || attachmentUsageHours === null || attachmentUsageHours === '') {
    return 'Enter attachment usage when an attachment is selected.';
  }
  const usage = Number(attachmentUsageHours);
  if (!Number.isFinite(usage) || !(usage > 0)) {
    return 'Attachment usage must be greater than zero.';
  }

  if (bookingType === 'hourly') {
    if (usage > ATTACHMENT_HOURS_SANITY_MAX) {
      return `Attachment hours cannot exceed ${ATTACHMENT_HOURS_SANITY_MAX} hours.`;
    }
    return attachmentMinHoursGateError({
      machine,
      selectedAttachment,
      bookedHours: usage,
    });
  }

  const feasibility = attachmentDurationFeasibilityError({
    machine,
    bookingType,
    quantity,
    selectedAttachment,
  });
  if (feasibility) return feasibility;
  const totalHours = bookedJobHoursEquiv(machine, bookingType, quantity);
  if (usage > totalHours) {
    return `Attachment usage cannot exceed the booked job duration (${formatHoursHm(totalHours)}).`;
  }
  return attachmentMinHoursGateError({
    machine,
    selectedAttachment,
    bookedHours: usage,
  });
}

/**
 * @param {object} [placeOpts] optional; may include selectedAttachment | attachmentUsageHours
 */
export function computeBookingQuote(machine, bookingType, quantity, placeOpts = {}) {
  const selectedAttachment = placeOpts.selectedAttachment ?? null;
  const attachmentUsageHours = placeOpts.attachmentUsageHours ?? null;
  // Hourly + attachment: machine base is bucket-only hours, not full T.
  const usageNumForBucket = Number(attachmentUsageHours);
  const bucketOnlyHours = (bookingType === 'hourly'
    && selectedAttachment
    && Number.isFinite(usageNumForBucket)
    && usageNumForBucket > 0)
    ? Number(quantity) - usageNumForBucket
    : quantity;
  const unitBase = calcBookingUnitBase(machine, bookingType, bucketOnlyHours);
  const attachmentSurcharge = calcAttachmentSurcharge(
    machine,
    selectedAttachment,
    attachmentUsageHours,
  );
  const base = Math.round(unitBase + attachmentSurcharge);
  const taxQuote = computeBookingTaxQuote(base, {
    recipientGstin: placeOpts.recipientGstin,
    siteStateCode: placeOpts.siteStateCode,
  });
  const gst = taxQuote.gst;
  const cgst = taxQuote.cgst;
  const sgst = taxQuote.sgst;
  const igst = taxQuote.igst;
  const interState = taxQuote.interState;
  const isGstApplicable = true;
  const grandTotal = taxQuote.grandTotal;
  const advanceRequired = Math.max(1, Math.ceil(grandTotal * BOOKING_ADVANCE_PERCENT));
  const attHourly = selectedAttachment && machine?.attachmentRates
    ? Number(machine.attachmentRates[selectedAttachment] || 0)
    : 0;
  return {
    base,
    gst,
    cgst,
    sgst,
    igst,
    interState,
    grandTotal,
    advanceRequired,
    gstApplicable: isGstApplicable,
    unitBase: Math.round(unitBase * 100) / 100,
    attachmentSurcharge: Math.round(attachmentSurcharge * 100) / 100,
    attachmentHourly: attHourly > 0 ? attHourly : 0,
    attachmentUsageHours: (() => {
      const n = Number(attachmentUsageHours);
      return Number.isFinite(n) && n > 0 ? n : null;
    })(),
  };
}
